import { FC, useCallback, useEffect, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { omit } from 'lodash-es';

import Loadable from '@components/Loadable/Loadable';
import { IUserEnteredSupplementAddNote } from '@interfaces/supplements';
import { handleGetUserEnteredSupplement } from '@store/features/inquiryResponse/actions';
import {
  inquiryResponseUserEnteredSupplementDataSelector,
  inquiryResponseUserEnteredSupplementErrorSelector,
  inquiryResponseUserEnteredSupplementIsLoadingSelector,
} from '@store/features/inquiryResponse/selectors';
import useDispatch from '@store/hooks/useDispatch';

import SupplementNeedsInfoForm from './SupplementNeedsInfoForm/SupplementNeedsInfoForm';
import { ISupplementNeedsInfoProps } from './SupplementNeedsInfo.interface';

const SupplementNeedsInfo: FC<ISupplementNeedsInfoProps> = ({
  userEnteredSupplementId,
  hasUnseenResponses,
  isDeactivated,
  isLastOneThatNeedsInfo,
  adminComments,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const userEnteredSupplement = useSelector(inquiryResponseUserEnteredSupplementDataSelector);
  const userEnteredSupplementIsLoading = useSelector(
    inquiryResponseUserEnteredSupplementIsLoadingSelector,
  );
  const userEnteredSupplementError = useSelector(inquiryResponseUserEnteredSupplementErrorSelector);

  const handleFetchUserEnteredSupplement = useCallback(() => {
    if (userEnteredSupplementId) dispatch(handleGetUserEnteredSupplement(userEnteredSupplementId));
  }, [dispatch, userEnteredSupplementId]);

  useEffect(handleFetchUserEnteredSupplement, [handleFetchUserEnteredSupplement]);

  const initialValues = useMemo(
    () => (userEnteredSupplement ? omit(userEnteredSupplement, ['id']) : {}),
    [userEnteredSupplement],
  );

  const handleValidateNeedsInfo = useCallback((values: IUserEnteredSupplementAddNote) => {
    const errors: Partial<Record<keyof IUserEnteredSupplementAddNote, string | boolean>> = {};

    if (!values.supplementName) errors.supplementName = true;
    if (!values.companyName) errors.companyName = true;
    if (!values.linkToProductPage) errors.linkToProductPage = true;
    if (!values.note) errors.note = true;

    return errors;
  }, []);

  return (
    <Loadable
      isLoading={userEnteredSupplementIsLoading}
      error={userEnteredSupplementError && t('errors.userEnteredSupplementError')}
      onRetry={handleFetchUserEnteredSupplement}
    >
      <Form
        // @ts-ignore because of custom props
        component={SupplementNeedsInfoForm}
        adminComments={adminComments}
        hasUnseenResponses={hasUnseenResponses}
        isDeactivated={isDeactivated}
        isLastOneThatNeedsInfo={isLastOneThatNeedsInfo}
        initialValues={initialValues}
        validate={handleValidateNeedsInfo}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </Loadable>
  );
};

export default SupplementNeedsInfo;
